import React, { useEffect } from 'react';
import { StepProps, TaxablePersonDetails } from '../../types';
import { Input } from '../ui/Input';
import { RadioGroup } from '../ui/RadioGroup';
import { Select } from '../ui/Select';
import { safeParseFloat, getAccountingBasis } from '../../utils/calculations';


const SectionDivider: React.FC<{ title: string }> = ({ title }) => (
  <div className="mt-6 mb-3 border-b border-[#b98e4c] pb-2">
    <h3 className="text-lg font-medium text-[#1a2c46]">{title}</h3>
  </div>
);


export const Step3Accounting: React.FC<StepProps> = ({ formData, onChange, errors, t }) => {
  const data = formData.accounting;
  const taxablePersonDetails = formData.taxablePersonDetails;

  const getRevenueString = (details: TaxablePersonDetails): string | undefined => {
    switch (details.filingAs) {
      case 'tax_group_parent':
        return details.taxGroupSpecifics?.consolidatedRevenue;
      case 'unincorporated_partnership':
        return details.unincorporatedPartnershipSpecifics?.revenueForTaxPeriod;
      case 'juridical_person':
      case 'free_zone_person':
        return details.juridicalPersonSpecifics?.revenueForTaxPeriod;
      case 'natural_person':
        return details.naturalPersonSpecifics?.turnover;
      default:
        return '0';
    }
  };
  

  const revenue = safeParseFloat(getRevenueString(taxablePersonDetails));
  const currentAccountingBasis = getAccountingBasis(taxablePersonDetails);
  
  const isQfzpHardRequirement = 
    ((taxablePersonDetails.filingAs === 'juridical_person' && taxablePersonDetails.juridicalPersonSpecifics?.isFreeZonePerson === 'yes') ||
     taxablePersonDetails.filingAs === 'free_zone_person') &&
    taxablePersonDetails.juridicalPersonSpecifics?.electionToForego0RateFZ === 'no' && 
    taxablePersonDetails.juridicalPersonSpecifics?.meetsQualifyingFZRequirements === 'yes';

  const isAuditMandatory = revenue > 50000000 || isQfzpHardRequirement;

  useEffect(() => {
    if (isAuditMandatory && data.financialStatementsAudited !== 'yes') {
      onChange('accounting', 'financialStatementsAudited', 'yes');
    }
  }, [isAuditMandatory, data.financialStatementsAudited, onChange]);


  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-[#1a2c46] mb-6">{t('accounting_schedule_title')}</h2>

      <SectionDivider title={t('accounting_income_statement_title')} />
      <Input
        label={t('accounting_revenue_label')}
        type="number"
        id="incomeStatement_revenue"
        name="incomeStatement_revenue"
        value={data.incomeStatement_revenue}
        onChange={(e) => onChange('accounting', 'incomeStatement_revenue', e.target.value)}
        error={errors.incomeStatement_revenue as string}
        placeholder={t('amount_aed_placeholder')}
      />
      <Input
        label={t('accounting_cost_of_sales_label')}
        type="number"
        id="incomeStatement_costOfSales"
        name="incomeStatement_costOfSales"
        value={data.incomeStatement_costOfSales}
        onChange={(e) => onChange('accounting', 'incomeStatement_costOfSales', e.target.value)}
        error={errors.incomeStatement_costOfSales as string}
        placeholder={t('amount_aed_placeholder')}
      />
       <Input
        label={t('accounting_gross_profit_label')}
        type="number"
        id="incomeStatement_grossProfit"
        name="incomeStatement_grossProfit"
        value={data.incomeStatement_grossProfit}
        onChange={(e) => onChange('accounting', 'incomeStatement_grossProfit', e.target.value)}
        error={errors.incomeStatement_grossProfit as string}
        placeholder={t('amount_aed_placeholder')}
      />
      <Input
        label={t('accounting_operating_expenses_label')}
        type="number"
        id="incomeStatement_operatingExpenses"
        name="incomeStatement_operatingExpenses"
        value={data.incomeStatement_operatingExpenses}
        onChange={(e) => onChange('accounting', 'incomeStatement_operatingExpenses', e.target.value)}
        error={errors.incomeStatement_operatingExpenses as string}
        placeholder={t('amount_aed_placeholder')}
      />
      <Input
        label={t('accounting_other_income_label')}
        type="number"
        id="incomeStatement_otherIncome"
        name="incomeStatement_otherIncome"
        value={data.incomeStatement_otherIncome}
        onChange={(e) => onChange('accounting', 'incomeStatement_otherIncome', e.target.value)}
        error={errors.incomeStatement_otherIncome as string}
        placeholder={t('amount_aed_placeholder')}
      />
      <Input
        label={t('accounting_income_label')}
        type="number"
        id="accountingIncome"
        name="accountingIncome"
        value={data.accountingIncome} 
        onChange={(e) => onChange('accounting', 'accountingIncome', e.target.value)}
        error={errors.accountingIncome as string}
        placeholder={t('accounting_income_placeholder')}
        helpText={t('help_s3_accounting_income')}
      />
      {currentAccountingBasis !== 'cash' && (
        <>
            <SectionDivider title={t('accounting_oci_title')} />
            <Input
                label={t('accounting_oci_items_net_tax_label')}
                type="number"
                id="oci_itemsNetOfTax"
                name="oci_itemsNetOfTax"
                value={data.oci_itemsNetOfTax}
                onChange={(e) => onChange('accounting', 'oci_itemsNetOfTax', e.target.value)}
                error={errors.oci_itemsNetOfTax as string}
                placeholder={t('amount_aed_placeholder')}
            />

            <SectionDivider title={t('accounting_balance_sheet_title')} />
            <Input
                label={t('accounting_total_assets_label')}
                type="number"
                id="balanceSheet_totalAssets"
                name="balanceSheet_totalAssets"
                value={data.balanceSheet_totalAssets}
                onChange={(e) => onChange('accounting', 'balanceSheet_totalAssets', e.target.value)}
                error={errors.balanceSheet_totalAssets as string}
                placeholder={t('amount_aed_placeholder')}
            />
            <Input
                label={t('accounting_total_liabilities_label')}
                type="number"
                id="balanceSheet_totalLiabilities"
                name="balanceSheet_totalLiabilities"
                value={data.balanceSheet_totalLiabilities}
                onChange={(e) => onChange('accounting', 'balanceSheet_totalLiabilities', e.target.value)}
                error={errors.balanceSheet_totalLiabilities as string}
                placeholder={t('amount_aed_placeholder')}
            />
            <Input
                label={t('accounting_total_equity_label')}
                type="number"
                id="balanceSheet_totalEquity"
                name="balanceSheet_totalEquity"
                value={data.balanceSheet_totalEquity}
                onChange={(e) => onChange('accounting', 'balanceSheet_totalEquity', e.target.value)}
                error={errors.balanceSheet_totalEquity as string}
                placeholder={t('amount_aed_placeholder')}
            />
        </>
      )}


      <SectionDivider title={t('accounting_audit_info_title')} />
      <RadioGroup
        label={t('financial_statements_audited_label')}
        name="financialStatementsAudited"
        value={data.financialStatementsAudited}
        onChange={(val) => { if (!isAuditMandatory) onChange('accounting', 'financialStatementsAudited', val); }}
        options={[{ label: t('yes'), value: 'yes' }, { label: t('no'), value: 'no' }]}
        error={errors.financialStatementsAudited as string}
        t={t}
        disabled={isAuditMandatory}
        helpText={isAuditMandatory ? t('audit_mandatory_note') : ''}
      />

      {data.financialStatementsAudited === 'yes' && (
        <>
          <Select
            label={t('audit_opinion_label')}
            id="auditOpinion"
            name="auditOpinion"
            value={data.auditOpinion || ''}
            onChange={(e) => onChange('accounting', 'auditOpinion', e.target.value)}
            error={errors.auditOpinion as string}
            options={[
              { value: '', label: t('select_option') },
              { value: 'unqualified', label: t('unqualified_opinion') },
              { value: 'qualified', label: t('qualified_opinion') },
            ]}
            helpText={t('help_s3_audit_opinion')}
          />
          <Input
            label={t('auditor_name_label')}
            type="text"
            id="auditorName"
            name="auditorName"
            value={data.auditorName || ''}
            onChange={(e) => onChange('accounting', 'auditorName', e.target.value)}
            error={errors.auditorName as string}
            placeholder={t('auditor_name_placeholder')}
          />
        </>
      )}

      <p className="mt-4 text-sm text-gray-600">{t('step3_note')}</p>
      {formData.elections.smallBusinessRelief && (
        <p className="mt-2 p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-md text-sm">
          {t('sbr_skip_note')}
        </p>
      )}
    </div>
  );
};